// Marketing-only layout: simple top bar, footer, no auth.
import { useState, type ReactNode } from "react";
import { Link, useRoute, appUrl } from "../lib/router";
import { useTheme } from "../lib/theme";
import { trackEvent } from "../lib/apteva";

const APP_URL = (typeof window !== "undefined" && (window as any).__APP_URL__) || "https://app.socialcast.dev";

function ThemeToggle() {
  const { resolved, setTheme } = useTheme();
  return (
    <button
      onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
      className="p-2 rounded-lg text-stone-400 dark:text-zinc-500 hover:bg-stone-100 dark:hover:bg-zinc-800 hover:text-stone-600 dark:hover:text-zinc-300 transition-colors"
      title={`Switch to ${resolved === "dark" ? "light" : "dark"} mode`}
    >
      {resolved === "dark" ? (
        <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const route = useRoute();
  const currentPath = route.split("?")[0].replace(/\/+$/, "") || "/";
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-zinc-950 transition-colors">
      <div className="px-4 py-2 text-center text-sm bg-orange-50 text-orange-950 dark:bg-zinc-900 dark:text-orange-200">
        Early access: create a free account to register your interest. Multichannel publishing is in development.
      </div>
      <nav className="bg-white/80 dark:bg-zinc-900/80 glass border-b border-stone-200/60 dark:border-zinc-800/60 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/" className="flex items-center gap-2.5">
              <img src="/brand/logo-20260908.png" width={32} height={32} alt="" aria-hidden="true" className="w-8 h-8 shrink-0 rounded-lg" />
              <span className="text-stone-900 dark:text-white font-bold text-lg tracking-tight">SocialCast</span>
            </Link>
            <div className="hidden md:flex items-center gap-1">
              <Link href="/features" className="px-3 py-2 rounded-lg text-sm font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                Features
              </Link>
              <Link href="/pricing" className="px-3 py-2 rounded-lg text-sm font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                Pricing
              </Link>
              <Link href="/compare/ayrshare" className="hidden sm:inline-flex px-3 py-2 rounded-lg text-sm font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                Compare
              </Link>
              <Link href="/blog" className="px-3 py-2 rounded-lg text-sm font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                Blog
              </Link>
              <ThemeToggle />
              <a
                href={`${APP_URL}/login`}
                onClick={(event) => { event.currentTarget.href = appUrl("/login"); trackEvent("socialcast_site_link_click", { href: `${APP_URL}/login`, label: "Login", source_path: currentPath, target: "app" }); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-stone-600 dark:text-zinc-400 hover:text-stone-900 dark:hover:text-white transition-colors"
              >
                Login
              </a>
              <a
                href={`${APP_URL}/register`}
                onClick={(event) => { event.currentTarget.href = appUrl("/register"); trackEvent("socialcast_site_link_click", { href: `${APP_URL}/register`, label: "Get Started", source_path: currentPath, target: "app" }); }}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-all"
              >
                Get Started
              </a>
            </div>
            <div className="flex md:hidden items-center gap-1">
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-stone-500 dark:text-zinc-400 hover:bg-stone-100 dark:hover:bg-zinc-800 transition-colors"
                aria-expanded={menuOpen}
                aria-label={menuOpen ? "Close navigation" : "Open navigation"}
              >
                {menuOpen ? (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16M4 12h16M4 17h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {menuOpen && (
            <div className="md:hidden border-t border-stone-200/70 dark:border-zinc-800/70 py-3">
              <div className="grid gap-1">
                <Link href="/features" onClick={closeMenu} className="px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800">Features</Link>
                <Link href="/pricing" onClick={closeMenu} className="px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800">Pricing</Link>
                <Link href="/compare/ayrshare" onClick={closeMenu} className="px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800">Compare</Link>
                <Link href="/blog" onClick={closeMenu} className="px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 dark:text-zinc-300 hover:bg-stone-100 dark:hover:bg-zinc-800">Blog</Link>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-stone-200/70 dark:border-zinc-800/70">
                <a
                  href={`${APP_URL}/login`}
                  onClick={(event) => {
                    event.currentTarget.href = appUrl("/login");
                    closeMenu();
                    trackEvent("socialcast_site_link_click", { href: `${APP_URL}/login`, label: "Login", source_path: currentPath, target: "app" });
                  }}
                  className="h-10 inline-flex items-center justify-center rounded-lg text-sm font-medium border border-stone-200 dark:border-zinc-700 text-stone-700 dark:text-zinc-300"
                >
                  Login
                </a>
                <a
                  href={`${APP_URL}/register`}
                  onClick={(event) => {
                    event.currentTarget.href = appUrl("/register");
                    closeMenu();
                    trackEvent("socialcast_site_link_click", { href: `${APP_URL}/register`, label: "Get Started", source_path: currentPath, target: "app" });
                  }}
                  className="h-10 inline-flex items-center justify-center rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600"
                >
                  Get Started
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
      <main className={["/", "/features", "/pricing"].includes(currentPath) ? "" : "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        {children}
      </main>
      <footer className="border-t border-stone-200/60 dark:border-zinc-800/60 py-6 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-400 dark:text-zinc-600">
          <Link href="/" className="inline-flex items-center gap-2">
            <img src="/brand/logo-20260908.png" width={24} height={24} alt="" aria-hidden="true" className="w-6 h-6 shrink-0 rounded" />
            <span>SocialCast</span>
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link href="/privacy" className="hover:text-stone-600 dark:hover:text-zinc-400 transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-stone-600 dark:hover:text-zinc-400 transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-stone-600 dark:hover:text-zinc-400 transition-colors">Blog</Link>
            <Link href="/compare/ayrshare" className="hover:text-stone-600 dark:hover:text-zinc-400 transition-colors">Compare</Link>
            <Link href="/data-deletion" className="hover:text-stone-600 dark:hover:text-zinc-400 transition-colors">Data Deletion</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
